import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, CalendarCheck, FileText, Quote, AlertCircle, Activity, Contact, FolderOpen } from "lucide-react";
import api from "@/lib/api";

function StatCard({ icon: Icon, label, value, to, accent }) {
  const body = (
    <div className="rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md">
      <div className="flex items-center justify-between">
        <span className={`flex h-11 w-11 items-center justify-center rounded-full ${accent}`}><Icon size={20} /></span>
        <span className="font-serif text-3xl font-bold text-navy">{value ?? "—"}</span>
      </div>
      <p className="mt-3 text-sm font-medium text-slate-600">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  const s = data?.stats || {};

  const CARDS = [
    { key: "active_clients", icon: Contact, label: "Active clients", to: "/admin/clients", accent: "bg-emerald-100 text-emerald-600" },
    { key: "open_matters", icon: FolderOpen, label: "Open matters", to: "/admin/matters", accent: "bg-navy/10 text-navy" },
    { key: "new_enquiries", icon: Inbox, label: "New enquiries", to: "/admin/enquiries", accent: "bg-gold/15 text-gold" },
    { key: "consultations", icon: CalendarCheck, label: "Consultations", to: "/admin/consultations", accent: "bg-blue-100 text-blue-600" },
    { key: "draft_articles", icon: FileText, label: "Draft articles", to: "/admin/articles", accent: "bg-amber-100 text-amber-600" },
    { key: "published_testimonials", icon: Quote, label: "Published testimonials", to: "/admin/testimonials", accent: "bg-purple-100 text-purple-600" },
  ];
  const cards = CARDS.filter((c) => c.key in s);

  return (
    <div>
      <h1 className="font-serif text-3xl font-bold text-navy">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Your role-specific overview of what needs attention today.</p>

      {cards.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c) => (
            <StatCard key={c.key} icon={c.icon} label={c.label} value={s[c.key]} to={c.to} accent={c.accent} />
          ))}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {(data?.recent_enquiries?.length >= 0 && ("new_enquiries" in s || "consultations" in s)) && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-navy"><Inbox size={18} className="text-gold" /> Recent enquiries</h2>
          <div className="space-y-3">
            {data?.recent_enquiries?.length ? data.recent_enquiries.map((e) => (
              <Link to={e.type === "consultation" ? "/admin/consultations" : "/admin/enquiries"} key={e.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
                <div>
                  <div className="text-sm font-medium text-navy">{e.name}</div>
                  <div className="text-xs text-slate-500">{e.service_area || "General enquiry"}</div>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{e.status}</span>
              </Link>
            )) : <p className="text-sm text-slate-400">No enquiries yet.</p>}
          </div>
        </div>
        )}

        {data?.recent_activity?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-navy"><Activity size={18} className="text-gold" /> Recent activity</h2>
          <div className="space-y-2.5">
            {data.recent_activity.map((a) => (
              <div key={a.id} className="flex items-start gap-3 text-sm">
                <AlertCircle size={15} className="mt-0.5 shrink-0 text-slate-300" />
                <div>
                  <span className="font-medium text-navy">{a.user}</span>
                  <span className="text-slate-500"> · {a.action} · {a.entity}</span>
                  {a.summary && <span className="text-slate-400"> — {a.summary}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
